import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, addDoc, updateDoc, doc, query, getDocs, Timestamp, getDoc, deleteDoc, writeBatch, where, setDoc } from 'firebase/firestore';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { X, Clock, Calendar, User, ChevronLeft, ChevronRight, Plus, Copy, Search, AlertCircle, Shield, Check, Menu } from 'lucide-react';
import ClientAutocomplete from './ClientAutocomplete';
import EnhancedAppointmentDetailsModal from './EnhancedAppointmentDetailsModal';
import StandardAddAppointmentModal from './StandardAddAppointmentModal';
import EnhancedEditAppointmentModal from './EnhancedEditAppointmentModal';
import { useTheme } from '../contexts/ThemeContext';
import EnhancedModal, { ModalHeader, ModalBody, ModalFooter, ModalButton } from './EnhancedModal';

// Hook personnalisé pour détecter la taille de l'écran
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
}

const WeekViewMobile = ({ db, currentDate, currentUser, onDateChange, showCompletedAppointments }) => {
  const { theme } = useTheme();

  // Vérifier si currentUser est null et retourner un message d'attente
  if (!currentUser || !currentUser.userId) {
    return (
      <div className="week-view flex flex-col h-full overflow-hidden">
        <div className="text-center text-gray-500 p-4">
          <p>Chargement des informations utilisateur...</p>
        </div>
      </div>
    );
  }

  const [selectedDay, setSelectedDay] = useState(new Date(currentDate));
  const [weekDates, setWeekDates] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isCtrlPressed, setIsCtrlPressed] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedEditAppointment, setSelectedEditAppointment] = useState(null);
  const [clients, setClients] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [adminRoles, setAdminRoles] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedUnassignedAppointment, setSelectedUnassignedAppointment] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedInfoAppointment, setSelectedInfoAppointment] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [clickTimer, setClickTimer] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDetailAppointment, setSelectedDetailAppointment] = useState(null);
  const clickDelay = 200; // Délai en millisecondes pour détecter un double-clic
  const [userPermissions, setUserPermissions] = useState({
    read: false,
    write: false,
    create: false,
    delete: false,
    manage: false
  });


  const getWeekDates = useCallback((date) => {
    const week = [];
    const current = new Date(date);
    current.setDate(current.getDate() - current.getDay() + (current.getDay() === 0 ? -6 : 1)); // Commence par lundi
    for (let i = 0; i < 7; i++) {
      week.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return week;
  }, []);

  useEffect(() => {
    const newSelectedDay = new Date(currentDate);
    setSelectedDay(newSelectedDay);
    setWeekDates(getWeekDates(newSelectedDay));
  }, [currentDate, getWeekDates]);

  const handleDaySelect = (date) => {
    setSelectedDay(date);
    onDateChange(date);
  };

  const navigateDay = (direction) => {
    const newDate = new Date(selectedDay);
    newDate.setDate(newDate.getDate() + direction);
    handleDaySelect(newDate);
  };

  const formatDay = (date) => {
    return date.toLocaleDateString('fr-FR', { weekday: 'short' }).charAt(0).toUpperCase();
  };

  const formatDayNumber = (date) => {
    return date.getDate();
  };

  const isCurrentDay = (date) => {
    return date.toDateString() === new Date().toDateString();
  };

  const isSelectedDay = (date) => {
    return date.toDateString() === selectedDay.toDateString();
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(selectedDay);
    newDate.setDate(newDate.getDate() + direction * 7);
    setSelectedDay(newDate);
    onDateChange(newDate);
  };


  const { width } = useWindowSize();
  const isMobile = width < 768; // Considère les écrans de moins de 768px comme mobiles

  const visibleEmployees = useMemo(() => {
    if (isMobile) {
      return employees.filter(emp => emp.userId === currentUser.userId);
    } else if (isAdmin) {
      return employees;
    } else {
      return employees.filter(emp => emp.userId === currentUser.userId);
    }
  }, [employees, isAdmin, currentUser, isMobile]);


  // Fonction pour récupérer les permissions de l'utilisateur
  const fetchUserPermissions = useCallback(async () => {
    if (!db || !currentUser) return;
    try {
      const userDoc = await getDoc(doc(db, 'employees', currentUser.userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userRoleId = userData.role;

        // Essayer d'abord le nouveau système advanced_roles
        try {
          const advancedRoleDoc = await getDoc(doc(db, 'advanced_roles', userRoleId));
          if (advancedRoleDoc.exists()) {
            const roleData = advancedRoleDoc.data();
            const appointmentsPerms = roleData.permissions?.appointments || {};
            setUserPermissions({
              read: appointmentsPerms.read || false,
              write: appointmentsPerms.write || false,
              create: appointmentsPerms.create || false,
              delete: appointmentsPerms.delete || false,
              manage: appointmentsPerms.manage || false
            });
            return;
          }
        } catch (advancedError) {
          console.log("Nouveau système de permissions non disponible, utilisation du fallback...");
        }

        // Fallback: ancien système
        const roleDoc = await getDoc(doc(db, 'roles', userRoleId));
        if (roleDoc.exists()) {
          const roleData = roleDoc.data();
          setUserPermissions({
            read: roleData.permissions.includes('read_appointments'),
            write: roleData.permissions.includes('write_appointments'),
            create: roleData.permissions.includes('write_appointments'),
            delete: roleData.permissions.includes('write_appointments'),
            manage: roleData.permissions.includes('write_appointments')
          });
        }
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des permissions de l'utilisateur:", error);
    }
  }, [db, currentUser]);

  useEffect(() => {
    fetchUserPermissions();
  }, [fetchUserPermissions]);


  // Fonction pour vérifier si l'utilisateur a une permission spécifique
  const hasPermission = (permission) => userPermissions[permission];


  const fetchUserRole = useCallback(async () => {
    if (!db || !currentUser) return;
    try {
      const userDoc = await getDoc(doc(db, 'employees', currentUser.userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        // Vérification pour un seul rôle
        const userRoleId = userData.role; // Changé de roles à role
        setUserRole(userRoleId);
        setIsAdmin(adminRoles.includes(userRoleId));
      }
    } catch (error) {
      console.error("Erreur lors de la récupération du rôle de l'utilisateur:", error);
    }
  }, [db, currentUser, adminRoles]);



  useEffect(() => {
    if (adminRoles.length > 0) {
      fetchUserRole();
    }
  }, [fetchUserRole, adminRoles]);

  const fetchEmployees = useCallback(async () => {
    if (!db) return;
    try {
      const employeesRef = collection(db, 'employees');
      const q = query(employeesRef, where('isArchived', '==', false));

      // Promesses parallèles : liste filtrée ET utilisateur courant spécifique
      const [querySnapshot, currentUserDoc] = await Promise.all([
        getDocs(q),
        currentUser ? getDoc(doc(db, 'employees', currentUser.userId)) : Promise.resolve(null)
      ]);

      // Map des employés filtrés
      const fetchedEmployeesMap = new Map();
      querySnapshot.docs.forEach(doc => {
        fetchedEmployeesMap.set(doc.id, {
          userId: doc.id,
          ...doc.data(),
          color: doc.data().color || '#808080',
          isAdmin: adminRoles.includes(doc.data().role)
        });
      });

      // AJOUT FORCE DE L'UTILISATEUR COURANT s'il existe et n'est pas déjà présent
      if (currentUserDoc && currentUserDoc.exists()) {
        const userData = currentUserDoc.data();
        fetchedEmployeesMap.set(currentUserDoc.id, {
          userId: currentUserDoc.id,
          ...userData,
          color: userData.color || '#808080',
          isAdmin: adminRoles.includes(userData.role)
        });
      }

      setEmployees(Array.from(fetchedEmployeesMap.values()));
    } catch (error) {
      console.error("Erreur lors de la récupération des employés:", error);
    }
  }, [db, adminRoles, currentUser]);

  useEffect(() => {
    if (adminRoles.length > 0) {
      fetchEmployees();
    }
  }, [fetchEmployees, adminRoles]);

  const renderEmployeeHeader = (employee) => {
    const isCurrentUser = employee.userId === currentUser.userId;
    const headerText = isCurrentUser ? "Mon planning" : (employee.name || `${employee.firstName} ${employee.lastName}`);

    return (
      <h3 className="font-bold text-center mb-4 text-lg flex items-center justify-center">
        {headerText}
        {employee.isAdmin && (
          <Shield className="ml-2 text-blue-500" size={20} title="Droits administratifs" />
        )}
      </h3>
    );
  };

  const filteredEmployees = useMemo(() => {
    if (isAdmin) {
      return employees;
    } else {
      return employees.filter(emp => emp.userId === currentUser.userId);
    }
  }, [employees, isAdmin, currentUser]);

  // Mise à jour de la fonction handleUnassignedAppointmentClick
  const handleUnassignedAppointmentClick = (appointment) => {
    if (appointment.status === 'libre' && (hasPermission('write') || hasPermission('read'))) {
      setSelectedUnassignedAppointment(appointment);
      setShowBookingModal(true);
      setShowAddModal(false);
    } else if (appointment.status === 'reserve') {
      setSelectedInfoAppointment(appointment);
      setShowInfoModal(true);
    }
  };

  const getDayIndex = (date) => {
    return weekDates.findIndex(weekDate =>
      weekDate.toDateString() === date.toDateString()
    );
  };


  const handleBookAppointment = async () => {
    if (!selectedUnassignedAppointment || !currentUser) return;

    try {
      const appointmentRef = doc(db, 'appointments', selectedUnassignedAppointment.id);
      await updateDoc(appointmentRef, {
        employeeId: currentUser.userId,
        status: 'reserve'
      });

      fetchAppointments();
      setShowBookingModal(false);
    } catch (error) {
      console.error("Erreur lors de la réservation du rendez-vous:", error);
      alert("Une erreur s'est produite lors de la réservation du rendez-vous");
    }
  };


  const fetchAppointments = useCallback(async () => {
    if (!db || !currentUser) return;
    try {
      const startOfDay = new Date(currentDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(currentDate);
      endOfDay.setHours(23, 59, 59, 999);

      let appointmentsQuery = query(
        collection(db, 'appointments'),
        where('date', '>=', Timestamp.fromDate(startOfDay)),
        where('date', '<=', Timestamp.fromDate(endOfDay))
      );

      const querySnapshot = await getDocs(appointmentsQuery);
      const fetchedAppointments = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate() || new Date(),
      }));

      const appointmentsWithClientInfo = await Promise.all(fetchedAppointments.map(async appointment => {
        const clientDoc = await getDoc(doc(db, 'clients', appointment.clientId));
        const clientData = clientDoc.data();
        return {
          ...appointment,
          clientName: clientData ? `${clientData.lastName} ${clientData.firstName}` : 'Client non spécifié',
          clientColor: clientData ? clientData.color : '#808080',
          clientAddress: clientData ? clientData.address : '',
          clientPhone: clientData ? clientData.phoneNumber : '',
          clientEmail: clientData ? clientData.email : '',
          status: appointment.status || 'libre'
        };
      }));
      setAppointments(appointmentsWithClientInfo);
    } catch (error) {
      console.error("Erreur lors de la récupération des rendez-vous:", error);
    }
  }, [db, currentDate, currentUser]);

  useEffect(() => {
    fetchEmployees();
    fetchAppointments();
  }, [fetchEmployees, fetchAppointments, adminRoles, userRole]);


  useEffect(() => {
    const fetchClients = async () => {
      if (!db) return;
      try {
        const clientsRef = collection(db, 'clients');
        const q = query(clientsRef, where('isActive', '==', true));
        const clientsSnapshot = await getDocs(q);
        const fetchedClients = clientsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setClients(fetchedClients);
      } catch (error) {
        console.error("Erreur lors de la récupération des clients:", error);
      }
    };
    fetchClients();
  }, [db]);


  const filteredAppointments = useMemo(() => {
    return appointments.filter(appointment => {
      const searchText = filterText.toLowerCase();
      const matchesSearch = appointment.clientName.toLowerCase().includes(searchText) ||
        (appointment.employeeName || '').toLowerCase().includes(searchText) ||
        appointment.startTime.includes(searchText) ||
        appointment.endTime.includes(searchText);

      // Inclure les rendez-vous terminés si l'option est activée
      const showCompleted = showCompletedAppointments || appointment.status !== 'completed';

      return matchesSearch && showCompleted;
    });
  }, [appointments, filterText, showCompletedAppointments]);

  const groupedAppointments = useMemo(() => {
    const grouped = {};
    employees.forEach(employee => {
      grouped[employee.userId] = filteredAppointments.filter(app =>
        app.employeeId === employee.userId
      ).sort((a, b) => a.startTime.localeCompare(b.startTime));
    });
    return grouped;
  }, [filteredAppointments, employees]);

  const handleDragEnd = (result) => {
    // Implémentez la logique de drag and drop ici
  };

  const sortedUnassignedAppointments = useMemo(() => {
    return filteredAppointments
      .filter(app => !app.employeeId || app.status === 'libre' || app.status === 'reserve')
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  }, [filteredAppointments]);

  const groupedAssignedAppointments = useMemo(() => {
    const grouped = {};
    employees.forEach(employee => {
      grouped[employee.userId] = filteredAppointments
        .filter(app => app.employeeId === employee.userId &&
          (app.status === 'assigne' || app.status === 'in_progress' ||
            (app.status === 'completed' && showCompletedAppointments)))
        .sort((a, b) => a.startTime.localeCompare(b.startTime));
    });
    return grouped;
  }, [filteredAppointments, employees, showCompletedAppointments]);

  const handleAppointmentClick = (appointment, event) => {
    event.preventDefault();
    event.stopPropagation();

    if (clickTimer) {
      // Double-clic détecté
      clearTimeout(clickTimer);
      setClickTimer(null);
      handleAppointmentDoubleClick(appointment);
    } else {
      // Définir un timer pour le simple clic
      const timer = setTimeout(() => {
        handleSingleClick(appointment);
        setClickTimer(null);
      }, clickDelay);
      setClickTimer(timer);
    }
  };

  const handleSingleClick = (appointment) => {
    if (appointment.status === 'libre') {
      setSelectedUnassignedAppointment(appointment);
      setShowBookingModal(true);
    } else if (appointment.status === 'reserve') {
      setSelectedInfoAppointment(appointment);
      setShowInfoModal(true);
    } else {
      setSelectedDetailAppointment(appointment);
      setShowDetailsModal(true);
    }
  };

  const handleAppointmentDoubleClick = (appointment) => {
    if (hasPermission('write')) {
      setSelectedEditAppointment(appointment);
      setShowEditModal(true);
    }
  };


  const handleAppointmentUpdate = (updatedAppointment) => {
    setAppointments(prevAppointments =>
      prevAppointments.map(app =>
        app.id === updatedAppointment.id ? updatedAppointment : app
      )
    );
  };

  const handleAddAppointment = async (appointmentData) => {
    try {
      const newAppointmentRef = doc(collection(db, 'appointments'));
      await setDoc(newAppointmentRef, {
        ...appointmentData,
        date: Timestamp.fromDate(currentDate)
      });

      fetchAppointments();
      setShowAddModal(false);
    } catch (error) {
      console.error("Erreur lors de l'ajout du rendez-vous:", error);
    }
  };


  const handleApproveAppointment = async () => {
    if (!selectedInfoAppointment || !hasPermission('write')) return;

    try {
      const appointmentRef = doc(db, 'appointments', selectedInfoAppointment.id);
      await updateDoc(appointmentRef, {
        status: 'assigne'
      });

      setAppointments(prevAppointments =>
        prevAppointments.map(app =>
          app.id === selectedInfoAppointment.id ? { ...app, status: 'assigne' } : app
        )
      );

      setShowInfoModal(false);
      fetchAppointments();
    } catch (error) {
      console.error("Erreur lors de l'approbation du rendez-vous:", error);
      alert("Une erreur s'est produite lors de l'approbation du rendez-vous");
    }
  };

  const handleRejectAppointment = async () => {
    if (!selectedInfoAppointment || !hasPermission('write')) return;

    try {
      const appointmentRef = doc(db, 'appointments', selectedInfoAppointment.id);
      await updateDoc(appointmentRef, {
        status: 'libre',
        employeeId: null
      });

      fetchAppointments();
      setShowInfoModal(false);
    } catch (error) {
      console.error("Erreur lors du refus du rendez-vous:", error);
      alert("Une erreur s'est produite lors du refus du rendez-vous");
    }
  };

  const handleCancelReservation = async () => {
    if (!selectedInfoAppointment || selectedInfoAppointment.employeeId !== currentUser.userId) return;

    try {
      const appointmentRef = doc(db, 'appointments', selectedInfoAppointment.id);
      await updateDoc(appointmentRef, {
        status: 'libre',
        employeeId: null
      });

      fetchAppointments();
      setShowInfoModal(false);
    } catch (error) {
      console.error("Erreur lors de l'annulation de la réservation:", error);
      alert("Une erreur s'est produite lors de l'annulation de la réservation");
    }
  };

  const handleUpdateAppointment = async (updatedAppointment) => {
    try {
      const appointmentRef = doc(db, 'appointments', updatedAppointment.id);
      await updateDoc(appointmentRef, {
        ...updatedAppointment,
        date: Timestamp.fromDate(currentDate)
      });

      fetchAppointments();
      setShowEditModal(false);
    } catch (error) {
      console.error("Erreur lors de la mise à jour du rendez-vous:", error);
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    try {
      await deleteDoc(doc(db, 'appointments', appointmentId));
      fetchAppointments();
      setShowEditModal(false);
    } catch (error) {
      console.error("Erreur lors de la suppression du rendez-vous:", error);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ne pas intercepter les touches Control si l'utilisateur est dans un champ de formulaire
      if (e.key === 'Control' && !isFormField(e.target)) {
        setIsCtrlPressed(true);
      }
    };
    const handleKeyUp = (e) => {
      if (e.key === 'Control') {
        setIsCtrlPressed(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Fonction utilitaire pour vérifier si l'élément est un champ de formulaire
  const isFormField = (element) => {
    const formFieldTags = ['INPUT', 'TEXTAREA', 'SELECT'];
    return formFieldTags.includes(element.tagName) || element.isContentEditable;
  };

  const onDragEnd = useCallback(async (result) => {
    if (!result.destination || !isAdmin) return;

    const { source, destination, draggableId } = result;

    const sourceEmployeeId = source.droppableId;
    const destEmployeeId = destination.droppableId;

    const appointment = appointments.find(app => app.id === draggableId);
    if (!appointment) return;

    try {
      const appointmentRef = doc(db, 'appointments', appointment.id);
      let updateData = {};

      if (destEmployeeId === 'unassigned') {
        // Déplacer vers les plages à combler
        updateData = {
          employeeId: null,
          status: 'libre'
        };
      } else if (sourceEmployeeId === 'unassigned' || sourceEmployeeId !== destEmployeeId) {
        // Déplacer vers un employé (depuis non assigné ou un autre employé)
        updateData = {
          employeeId: destEmployeeId,
          status: 'assigne'
        };
      }

      if (Object.keys(updateData).length > 0) {
        await updateDoc(appointmentRef, updateData);

        // Mettre à jour l'état local
        setAppointments(prevAppointments =>
          prevAppointments.map(app =>
            app.id === appointment.id ? { ...app, ...updateData } : app
          )
        );
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour du rendez-vous:", error);
    }
  }, [appointments, db, isAdmin]);

  useEffect(() => {
    console.log("Filtered appointments:", filteredAppointments);
    console.log("Sorted unassigned appointments:", sortedUnassignedAppointments);
    console.log("Grouped assigned appointments:", groupedAssignedAppointments);
  }, [filteredAppointments, sortedUnassignedAppointments, groupedAssignedAppointments]);

  const getEmployeeName = useCallback((employeeId) => {
    const employee = employees.find(emp => emp.userId === employeeId);
    return employee ? (employee.name || `${employee.firstName} ${employee.lastName}`) : 'Employé non trouvé';
  }, [employees]);

  const renderAppointment = (appointment) => {
    let statusClass, statusText, statusIcon;

    switch (appointment.status) {
      case 'libre':
        statusClass = 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300';
        statusText = 'À combler';
        statusIcon = '⚠️';
        break;
      case 'reserve':
        statusClass = 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
        statusText = 'En attente';
        statusIcon = '⏳';
        break;
      case 'in_progress':
        statusClass = 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
        statusText = 'En cours';
        statusIcon = '▶️';
        break;
      case 'completed':
        statusClass = 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300';
        statusText = 'Terminé';
        statusIcon = '✅';
        break;
      default:
        statusClass = 'bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
        statusText = appointment.status || 'Assigné';
        statusIcon = '📅';
    }

    // Déterminer la couleur de la bordure gauche
    const borderColor = appointment.clientColor || (appointment.status === 'libre' ? '#ef4444' : '#6b7280');

    // Style pour l'indicateur de temps
    const timeStyle = "bg-gray-50 dark:bg-white/5 px-2 py-1 rounded text-xs font-medium text-gray-600 dark:text-gray-300 flex items-center gap-1.5";

    return (
      <div
        className={`group relative mb-2 rounded-xl bg-white dark:bg-gray-800 shadow-sm border border-gray-100 dark:border-gray-700/50 hover:shadow-md transition-all duration-200 active:scale-[0.98] overflow-hidden ${appointment.status === 'completed' ? 'opacity-75' : ''}`}
        style={{
          borderLeftWidth: '4px',
          borderLeftColor: borderColor
        }}
        onClick={(e) => handleAppointmentClick(appointment, e)}
      >
        <div className="p-3">
          {/* En-tête : Client et Statut */}
          <div className="flex justify-between items-start mb-2">
            <div className="flex items-center gap-2 overflow-hidden pr-2">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-50 dark:bg-gray-700 grid place-items-center text-gray-500 dark:text-gray-400">
                <User size={12} strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <h4 className="font-semibold text-xs text-gray-900 dark:text-gray-100 truncate leading-tight">
                  {appointment.clientName || "Client non spécifié"}
                </h4>
              </div>
            </div>

            {statusIcon && (
              <span className={`flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex items-center gap-1 ${statusClass}`}>
                {statusIcon}
              </span>
            )}
          </div>

          {/* Corps : Horaire */}
          <div className="flex items-center justify-between mb-1">
            <div className={timeStyle}>
              <Clock size={10} className="text-gray-400" />
              <span>{appointment.startTime} - {appointment.endTime}</span>
            </div>
          </div>

          {/* Employé (si assigné) */}
          {appointment.employeeId && (
            <div className="flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400 mt-1.5 px-0.5">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
              <span className="truncate max-w-[120px]">{getEmployeeName(appointment.employeeId)}</span>
            </div>
          )}

          {/* Indicateurs (Notes, Check-in) - Version compacte pour semaine */}
          {((appointment.notes && appointment.notes.content) || (appointment.checkInStatus)) && (
            <div className="flex gap-2 mt-2 pt-1.5 border-t border-gray-100 dark:border-gray-700/50">
              {(appointment.notes && appointment.notes.content) && (
                <span className="block w-2 h-2 rounded-full bg-indigo-500" title="Notes"></span>
              )}
              {appointment.checkInStatus && appointment.checkInStatus.checkInTime && (
                <Check size={10} className="text-emerald-500" strokeWidth={3} title="Pointé" />
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Fonction utilitaire pour assombrir une couleur
  const darkenColor = (color, percent) => {
    const num = parseInt(color.replace("#", ""), 16),
      amt = Math.round(2.55 * percent),
      R = Math.max((num >> 16) - amt, 0),
      G = Math.max((num >> 8 & 0x00FF) - amt, 0),
      B = Math.max((num & 0x0000FF) - amt, 0);
    return "#" + ((0x1000000 + (R * 0x10000) + (G * 0x100) + B).toString(16).slice(1));
  };

  // Fonction utilitaire pour déterminer si une couleur est claire ou foncée
  const isLightColor = (color) => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return brightness > 155;
  };

  return (
    <div className={`week-view flex flex-col h-full overflow-hidden ${theme === 'dark' ? 'dark' : ''}`}>
      <div className="week-navigation bg-gradient-to-r from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 border-b-2 border-gray-200 dark:border-gray-700 p-2 shadow-sm">
        <div className="grid grid-cols-7 gap-1">
          {weekDates.map((date, index) => {
            const dayAppointments = filteredAppointments.filter(app => {
              const appDate = app.date instanceof Date ? app.date : new Date(app.date);
              return appDate.toDateString() === date.toDateString() &&
                app.employeeId === currentUser.userId;
            });
            const isToday = date.toDateString() === new Date().toDateString();
            const isSelected = date.toDateString() === selectedDay.toDateString();
            const dayName = date.toLocaleDateString('fr-FR', { weekday: 'short' });

            return (
              <div
                key={index}
                className={`text-center cursor-pointer transition-all duration-300 p-1 rounded-lg ${isSelected
                  ? 'bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 shadow-md transform scale-105'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 hover:shadow-sm'
                  }`}
                onClick={() => handleDaySelect(date)}
              >
                <div className={`text-xs font-bold mb-1 tracking-wide ${isToday ? 'text-blue-600 dark:text-blue-400' :
                  isSelected ? 'text-blue-700 dark:text-blue-300' :
                    'text-gray-600 dark:text-gray-400'
                  }`}>
                  {dayName}
                </div>
                <div className={`text-sm font-extrabold p-1 rounded-full mx-auto w-8 h-8 flex items-center justify-center transition-all duration-300 ${isToday
                  ? 'bg-gradient-to-br from-blue-500 to-blue-700 text-white shadow-lg ring-2 ring-blue-300 dark:ring-blue-500 scale-110 animate-pulse'
                  : isSelected
                    ? 'bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-800 dark:to-blue-700 text-blue-800 dark:text-blue-100 border-2 border-blue-500 dark:border-blue-400 shadow-md scale-105'
                    : 'text-gray-800 dark:text-gray-200 hover:bg-gradient-to-br hover:from-gray-100 hover:to-gray-200 dark:hover:from-gray-700 dark:hover:to-gray-600'
                  }`}>
                  {date.getDate()}
                </div>


              </div>
            );
          })}
        </div>
      </div>


      {/* Contenu des rendez-vous */}
      <div className="content-wrapper flex-grow overflow-auto bg-gray-50 dark:bg-gray-900">
        <div className="p-4">
          <DragDropContext onDragEnd={onDragEnd}>
            {/* Section des rendez-vous non assignés - affichée uniquement s'il y en a */}
            {hasPermission('read') && sortedUnassignedAppointments.length > 0 && (
              <div className="wvm-unassigned-appointments mb-6">
                <h3 className="wvm-unassigned-title flex items-center">
                  <AlertCircle className="mr-2 text-yellow-500" size={20} />
                  Plages à combler :
                </h3>
                <Droppable droppableId="unassigned" direction="horizontal">
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`wvm-unassigned-list flex overflow-x-auto ${snapshot.isDraggingOver ? 'bg-blue-50 dark:bg-blue-900' : ''}`}
                      style={{ minHeight: '100px' }}
                    >
                      {sortedUnassignedAppointments.map((appointment, index) => (
                        <Draggable key={appointment.id} draggableId={appointment.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`mr-3 ${snapshot.isDragging ? 'opacity-50' : ''}`}
                              style={{
                                ...provided.draggableProps.style,
                                width: '200px',
                                flexShrink: 0
                              }}
                            >
                              {renderAppointment(appointment)}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            )}

            {/* Planning des employés */}
            <div className="employee-columns grid gap-4">
              {visibleEmployees.every(employee =>
                (!groupedAssignedAppointments[employee.userId] || groupedAssignedAppointments[employee.userId].length === 0)
              ) ? (
                <div className="empty-state flex flex-col items-center justify-center py-12">
                  <Calendar className={`mx-auto ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} mb-2`} size={48} />
                  <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Aucun rendez-vous ce jour</p>
                </div>
              ) : (
                visibleEmployees.map(employee => (
                  groupedAssignedAppointments[employee.userId] && groupedAssignedAppointments[employee.userId].length > 0 && (
                    <Droppable key={employee.userId} droppableId={employee.userId}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`employee-column p-4 rounded-lg bg-white dark:bg-gray-800`}
                        >
                          {renderEmployeeHeader(employee)}
                          {groupedAssignedAppointments[employee.userId].map((appointment, index) => (
                            <Draggable key={appointment.id} draggableId={appointment.id} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={`${snapshot.isDragging ? 'opacity-50' : ''}`}
                                  style={provided.draggableProps.style}
                                >
                                  {renderAppointment(appointment)}
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  )
                ))
              )}
            </div>
          </DragDropContext>
        </div>
      </div>


      {/* Modal pour la réservation de rendez-vous non assignés */}
      {showBookingModal && selectedUnassignedAppointment && (
        <EnhancedModal onClose={() => setShowBookingModal(false)} size="md">
          <ModalHeader>Réserver ce rendez-vous</ModalHeader>
          <ModalBody>
            <div className="space-y-3">
              <p className="text-gray-600 dark:text-gray-300">
                Voulez-vous réserver ce rendez-vous ?
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 space-y-2">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Client :</span>
                  <span className="text-gray-900 dark:text-gray-100">{selectedUnassignedAppointment.clientName || "Client non spécifié"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Date :</span>
                  <span className="text-gray-900 dark:text-gray-100">{selectedUnassignedAppointment.date.toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Heure :</span>
                  <span className="text-gray-900 dark:text-gray-100">{selectedUnassignedAppointment.startTime} - {selectedUnassignedAppointment.endTime}</span>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <ModalButton variant="secondary" onClick={() => setShowBookingModal(false)}>
              Annuler
            </ModalButton>
            <ModalButton variant="primary" onClick={handleBookAppointment}>
              Réserver
            </ModalButton>
          </ModalFooter>
        </EnhancedModal>
      )}

      {/* Modal d'information pour les rendez-vous en attente de validation */}
      {showInfoModal && selectedInfoAppointment && (
        <EnhancedModal onClose={() => setShowInfoModal(false)} size="md">
          <ModalHeader>Détails du rendez-vous</ModalHeader>
          <ModalBody>
            <div className="space-y-3">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 space-y-2">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Client :</span>
                  <span>{selectedInfoAppointment.clientName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Date :</span>
                  <span>{selectedInfoAppointment.date.toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Heure :</span>
                  <span>{selectedInfoAppointment.startTime} - {selectedInfoAppointment.endTime}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Employé :</span>
                  <span>{getEmployeeName(selectedInfoAppointment.employeeId)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Statut :</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedInfoAppointment.status === 'reserve'
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                    }`}>
                    {selectedInfoAppointment.status === 'reserve' ? 'En attente de validation' : selectedInfoAppointment.status}
                  </span>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            {selectedInfoAppointment.status === 'reserve' && (
              <>
                {hasPermission('write') && (
                  <>
                    <ModalButton variant="success" onClick={handleApproveAppointment}>
                      Approuver
                    </ModalButton>
                    <ModalButton variant="danger" onClick={handleRejectAppointment}>
                      Refuser
                    </ModalButton>
                  </>
                )}
                {selectedInfoAppointment.employeeId === currentUser.userId && (
                  <ModalButton variant="warning" onClick={handleCancelReservation}>
                    Annuler
                  </ModalButton>
                )}
              </>
            )}
            <ModalButton variant="secondary" onClick={() => setShowInfoModal(false)}>
              Fermer
            </ModalButton>
          </ModalFooter>
        </EnhancedModal>
      )}

      {showAddModal && (
        <StandardAddAppointmentModal
          date={currentDate}
          employees={employees}
          clients={clients}
          onSave={handleAddAppointment}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {showEditModal && selectedEditAppointment && (
        <EnhancedEditAppointmentModal
          appointment={selectedEditAppointment}
          employees={employees}
          clients={clients}
          onSave={handleUpdateAppointment}
          onClose={() => setShowEditModal(false)}
          onDelete={handleDeleteAppointment}
          canDelete={hasPermission('delete')}
        />
      )}

      {showDetailsModal && selectedDetailAppointment && (
        <EnhancedAppointmentDetailsModal
          appointment={selectedDetailAppointment}
          onClose={() => setShowDetailsModal(false)}
          onUpdate={(updatedAppointment) => {
            // Rafraîchir la liste des rendez-vous après mise à jour (pointage ou clôture)
            fetchAppointments();
            setShowDetailsModal(false);
          }}
          db={db}
        />
      )}
    </div>
  );
};


const AddAppointmentModal = ({ date, employees, clients, onSave, onClose }) => {
  const [appointmentData, setAppointmentData] = useState({
    date: date,
    startTime: '',
    endTime: '',
    employeeId: '',
    clientId: '',
    clientName: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAppointmentData(prev => ({ ...prev, [name]: value }));
  };

  const handleClientChange = (clientId, clientName) => {
    setAppointmentData(prev => ({ ...prev, clientId, clientName }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(appointmentData);
  };

  return (
    <div className="modal bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
      <h2 className="text-2xl font-bold mb-4">Ajouter un rendez-vous</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex items-center space-x-2">
          <Clock className="text-gray-400" size={20} />
          <input
            type="time"
            name="startTime"
            value={appointmentData.startTime}
            onChange={handleChange}
            className="flex-grow p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <span>à</span>
          <input
            type="time"
            name="endTime"
            value={appointmentData.endTime}
            onChange={handleChange}
            className="flex-grow p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-center space-x-2">
          <User className="text-gray-400" size={20} />
          <select
            name="employeeId"
            value={appointmentData.employeeId}
            onChange={handleChange}
            className="flex-grow p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Sélectionner un employé</option>
            {employees.map(emp => (
              <option key={emp.userId} value={emp.userId}>
                {emp.name || `${emp.firstName} ${emp.lastName}`}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center space-x-2">
          <User className="text-gray-400" size={20} />
          <ClientAutocomplete
            clients={clients}
            value={appointmentData.clientName}
            onChange={handleClientChange}
          />
        </div>
        <div className="flex justify-end space-x-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Ajouter
          </button>
        </div>
      </form>
    </div>
  );
};

const EditAppointmentModal = ({ appointment, employees, clients, onSave, onClose, onDelete }) => {
  const [appointmentData, setAppointmentData] = useState({
    ...appointment,
    date: appointment.date instanceof Date
      ? appointment.date
      : new Date(appointment.date)
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAppointmentData(prev => ({ ...prev, [name]: value }));
  };

  const handleClientChange = (clientId, clientName) => {
    setAppointmentData(prev => ({
      ...prev,
      clientId: clientId,
      clientName: clientName
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(appointmentData);
  };

  return (
    <div className="modal max-w-md mx-auto">
      <h2 className="text-2xl font-bold mb-4">Modifier le rendez-vous</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex items-center space-x-2">
          <Clock className="text-gray-400" size={20} />
          <input
            type="time"
            name="startTime"
            value={appointmentData.startTime}
            onChange={handleChange}
            className="flex-grow p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <span>à</span>
          <input
            type="time"
            name="endTime"
            value={appointmentData.endTime}
            onChange={handleChange}
            className="flex-grow p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-center space-x-2">
          <User className="text-gray-400" size={20} />
          <select
            name="employeeId"
            value={appointmentData.employeeId || 'unassigned'}
            onChange={handleChange}
            className="flex-grow p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="unassigned">Non assigné</option>
            {employees.map(emp => (
              <option key={emp.userId} value={emp.userId}>
                {emp.name || `${emp.firstName} ${emp.lastName}`}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center space-x-2">
          <User className="text-gray-400" size={20} />
          <ClientAutocomplete
            clients={clients}
            value={appointmentData.clientName || ''}
            onChange={handleClientChange}
          />
        </div>
        <div className="flex justify-end space-x-2 mt-6">
          <button
            type="button"
            onClick={() => onDelete(appointmentData.id)}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            Supprimer
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Mettre à jour
          </button>
        </div>
      </form>
    </div>
  );
};

// Fonction pour éclaircir une couleur
const lightenColor = (color, amount) => {
  return '#' + color.replace(/^#/, '').replace(/../g, color => ('0' + Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(16)).substr(-2));
};

// Fonction utilitaire pour déterminer si une couleur est claire ou foncée
const isLightColor = (color) => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return brightness > 155;
};

export default WeekViewMobile;
